import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Upload, X, Sparkles, CheckCircle2, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth } from "@/App";
import MarketingNav from "@/components/MarketingNav";
import MarketingFooter from "@/components/MarketingFooter";
import LegalAcceptanceCheckbox, { useLegalAcceptance } from "@/components/LegalAcceptanceCheckbox";
import { LegalAcceptanceError, describeAcceptanceFailure } from "@/lib/legalAcceptance";
import { REVIEW_TIERS, REVIEW_PRICE_FROM } from "@/lib/reviewPricing";

/**
 * BecomeExpertPage — Phase 2B restructure.
 *
 * Marketing-first layout that matches `_design-system/pages/become-expert.html`:
 *   Hero with "where Xpert is today" right rail → Comparison vs gig platforms
 *   → 4-step How → 7 Categories → 3 Time-commitment lanes → FAQ → existing
 *   application form under #apply.
 *
 * Concierge mode (2026-10-09): the four invented expert profiles (names,
 * quotes, earnings), the sample earnings dashboard, the "15 active experts"
 * and "~20m median" stats, typical-earnings ranges and categories marked
 * active without a reviewer were deleted. Never invent supply-side numbers.
 *
 * Backend contract (`/api/experts/apply`) and every formData key is preserved
 * byte-for-byte from the prior version — only chrome moves around it.
 */

const CURRENCIES = [
  { value: "USD", label: "USD - US Dollar" },
  { value: "INR", label: "INR - Indian Rupee" },
  { value: "EUR", label: "EUR - Euro" },
  { value: "GBP", label: "GBP - British Pound" },
  { value: "AED", label: "AED - UAE Dirham" },
  { value: "SGD", label: "SGD - Singapore Dollar" },
];

const V1_CATEGORIES = [
  "Law", "Product", "Engineering", "Finance",
  "Fundraising", "Accounting & Bookkeeping", "Strategy Reviews",
  "Code Reviews", "Ship Your Vibe-Coded App",
];

// `/become-expert?role=reviewer` (linked from /reviewers) preselects the code
// wedge. The chips hold the human labels; the backend maps them to the keys
// code_review, ship_app and engineering (normalize_review_categories).
const REVIEWER_ROLE_CATEGORIES = ["Code Reviews", "Ship Your Vibe-Coded App", "Engineering"];
const REVIEWER_RETURN_TO = encodeURIComponent("/become-expert?role=reviewer");

const AI_TOOLS = [
  "ChatGPT", "Claude", "Cursor", "Gemini",
  "Copilot", "Harvey", "v0", "Lovable",
];

export default function BecomeExpertPage() {
  const navigate = useNavigate();
  const { user, fetchUser } = useAuth();
  const fileInputRef = useRef(null);
  const [searchParams] = useSearchParams();
  const reviewerRole = searchParams.get("role") === "reviewer";

  const [loading, setLoading]         = useState(false);
  const [uploading, setUploading]     = useState(false);
  const [categories, setCategories]   = useState({ industries: [], expertise: [] });
  const [imageOption, setImageOption] = useState("upload");

  const [formData, setFormData] = useState({
    title: "",
    bio: "",
    expertise: [],
    industries: [],
    review_categories: reviewerRole ? [...REVIEWER_ROLE_CATEGORIES] : [],
    review_ai_tools: [],
    review_available: true,
    hourly_rate: "",
    review_price_usd: "",
    currency: "USD",
    meeting_link: "",
    linkedin: "",
    image_url: "",
    timezone: "Asia/Dubai",
    stay_anonymous: false,
  });

  useEffect(() => { fetchCategories(); }, []);

  // The initial state above covers a direct load of ?role=reviewer. This covers
  // client-side navigation onto the same mounted page: add the review options
  // without removing anything the applicant already picked.
  useEffect(() => {
    if (!reviewerRole) return;
    setFormData((p) => {
      const missing = REVIEWER_ROLE_CATEGORIES.filter((c) => !p.review_categories.includes(c));
      if (missing.length === 0 && p.review_available) return p;
      return { ...p, review_available: true, review_categories: [...p.review_categories, ...missing] };
    });
  }, [reviewerRole]);

  useEffect(() => {
    const hr = parseFloat(formData.hourly_rate);
    if (!isNaN(hr) && hr > 0 && !formData.review_price_usd) {
      const suggested = Math.max(49, Math.min(149, Math.round(hr / 3)));
      setFormData((p) => ({ ...p, review_price_usd: String(suggested) }));
    }
  }, [formData.hourly_rate]); // eslint-disable-line

  async function fetchCategories() {
    try {
      const { data } = await axios.get(`${API}/categories`);
      setCategories(data);
    } catch { /* noop */ }
  }

  const toggleListItem = (key, item) => {
    setFormData((p) => ({
      ...p,
      [key]: p[key].includes(item) ? p[key].filter((x) => x !== item) : [...p[key], item],
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp", "image/gif"].includes(file.type)) {
      toast.error("Invalid type. Use JPG/PNG/WebP/GIF.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Max file size is 5MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/upload/image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFormData((p) => ({ ...p, image_url: data.url }));
      toast.success("Image uploaded.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const [acceptedExpertLegal, setAcceptedExpertLegal] = useState(false);
  const [acceptanceError, setAcceptanceError] = useState(null);
  const { recordAll: recordExpertAcceptances } = useLegalAcceptance([
    "terms_of_service",
    "privacy_policy",
    "expert_agreement",
    "recording_consent",
  ]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error("Sign in to apply as an expert");
      navigate("/login");
      return;
    }
    if (!formData.title || !formData.bio || formData.expertise.length === 0 || !formData.hourly_rate) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (!acceptedExpertLegal) {
      toast.error("Please accept the Terms, Privacy Policy, Expert Agreement, and Recording & AI Consent");
      return;
    }
    setLoading(true);
    setAcceptanceError(null);
    try {
      // Record the agreements BEFORE submitting the application. If this
      // fails, nothing has been submitted yet, so there is no half-finished
      // application to explain: the applicant sees the error and clicks
      // Submit again (only the policies still missing are re-posted).
      try {
        await recordExpertAcceptances();
      } catch (recordErr) {
        if (!(recordErr instanceof LegalAcceptanceError)) throw recordErr;
        setAcceptanceError(describeAcceptanceFailure(recordErr));
        toast.error("We couldn't record your agreements, so your application was not submitted.");
        return;
      }
      const { data } = await axios.post(`${API}/experts/apply`, {
        ...formData,
        hourly_rate:      parseFloat(formData.hourly_rate),
        review_price_usd: formData.review_price_usd
          ? parseFloat(formData.review_price_usd)
          : null,
      });
      await fetchUser();
      toast.success("Application submitted.");
      navigate("/expert-onboarding-success", {
        state: {
          expertData: {
            id: data.expert_id,
            name: user?.name,
            title: formData.title,
            expertise: formData.expertise,
          },
        },
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to apply");
    } finally {
      setLoading(false);
    }
  };

  const scrollToApply = () => {
    const el = document.getElementById("apply");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen bg-canvas text-ink font-body">
      <MarketingNav />

      <Hero onApply={scrollToApply} />
      <Comparison />
      <HowItWorks />
      <Categories />
      <TimeCommitment />
      <FAQ />
      <ApplySection
        user={user}
        loading={loading}
        uploading={uploading}
        categories={categories}
        imageOption={imageOption}
        setImageOption={setImageOption}
        fileInputRef={fileInputRef}
        formData={formData}
        setFormData={setFormData}
        toggleListItem={toggleListItem}
        handleImageUpload={handleImageUpload}
        handleSubmit={handleSubmit}
        acceptanceError={acceptanceError}
        acceptedExpertLegal={acceptedExpertLegal}
        setAcceptedExpertLegal={setAcceptedExpertLegal}
        reviewerRole={reviewerRole}
      />

      <MarketingFooter />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hero
// ---------------------------------------------------------------------------

function Hero({ onApply }) {
  return (
    <section className="relative overflow-hidden">
      <div aria-hidden="true"
           className="absolute -top-32 -right-24 w-[640px] h-[640px] rounded-full bg-brand-green/10 blur-3xl pointer-events-none" />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-24 pb-16 sm:pb-24">
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-16 items-start">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand-green">
              The human review layer for AI-built work
            </p>
            <h1 className="font-display text-5xl sm:text-6xl text-ink leading-[1.05] mt-5">
              Be{" "}
              <em className="not-italic">
                <span className="italic text-brand-green">the green check.</span>
              </em>
            </h1>
            <p className="mt-6 text-lg text-slateline-600 leading-relaxed max-w-xl">
              Briefed clients. Right-sized work. AI brings them to you. Earn from your
              expertise without doing the marketing.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button onClick={onApply}
                      className="inline-flex items-center gap-2 h-12 px-6 rounded-md bg-brand-green hover:bg-brand-greenHover text-white font-semibold text-sm shadow-sm transition-colors">
                List your expertise <ArrowRight className="w-4 h-4" />
              </button>
              <a href="#how"
                 className="inline-flex items-center h-12 px-2 text-sm font-medium text-ink hover:text-brand-green transition-colors">
                See how it works ↓
              </a>
            </div>
            <div className="mt-10 pt-8 border-t border-slateline-200 grid grid-cols-3 gap-6">
              <HeroStat value="80%"   label="You keep" />
            </div>
          </div>

          <aside className="bg-white border border-slateline-200 rounded-xl p-7 shadow-sm">
            <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slateline-500 mb-4">
              Where Xpert is today
            </div>
            <div className="space-y-3 text-[15px] leading-relaxed text-slateline-700">
              <p>
                Xpert is early. Today every async review is done by the founder,
                Ankit Kumar, who covers AI-built code and product and strategy work.
              </p>
              <p>
                We add specialists by hand, as demand in their area grows. If that
                could be you, apply below and we'll reply personally.
              </p>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}

function HeroStat({ value, label }) {
  return (
    <div>
      <div className="font-display text-3xl font-bold text-ink leading-none">{value}</div>
      <div className="text-xs font-semibold uppercase tracking-wider text-slateline-500 mt-1.5">{label}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Comparison table — every other gig platform vs Xpert
// ---------------------------------------------------------------------------

const COMPARISON_ROWS = [
  ["Bid wars on price. Race to the bottom.",                              "No bidding. Written reviews pay list prices, set by turnaround."],
  ["15 minutes of context-setting on every call.",                        "Expert arrives knowing what AI tried, where it hedged, what to focus on."],
  ["40-hour engagements with proposals, scoping calls, intake forms.",    "One submission, one written verdict, on your schedule. Live sessions come later."],
  ["Build your own website. Run ads. Network constantly.",                "AI surfaces you when someone needs exactly your skill. Distribution included."],
  ["Platform fees plus connects, withdrawal fees, and bid-war discounting.", `Reviews are priced per turnaround (${REVIEW_TIERS.map((t) => `$${t.price}`).join(" / ")}) and you keep 80%. One flat 20% fee, no bidding, no connects, no hidden fees.`],
];

function Comparison() {
  return (
    <section className="bg-white border-t border-b border-slateline-200">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <SectionHead
          eyebrow="Why Xpert vs every other marketplace"
          title={<>Structurally different from <em className="not-italic"><span className="italic text-brand-green">every gig platform.</span></em></>}
          subtitle="If you've used Upwork, Fiverr, Toptal, Catalant — these are the structural traps we built around."
        />

        <div className="mt-10 bg-white border border-slateline-200 rounded-xl overflow-hidden">
          <div className="grid grid-cols-2 text-[11px] font-bold uppercase tracking-[0.14em] border-b border-slateline-200">
            <div className="p-5 bg-slateline-50 text-slateline-500">On other platforms</div>
            <div className="p-5 bg-brand-greenPale text-brand-green">On Xpert</div>
          </div>
          {COMPARISON_ROWS.map(([elsewhere, us], i) => (
            <div key={i} className="grid grid-cols-2 border-t border-slateline-200 first:border-t-0">
              <div className="p-5 text-sm text-slateline-500">{elsewhere}</div>
              <div className="p-5 text-sm text-ink font-medium bg-brand-greenPale border-l border-brand-green/20 flex gap-2.5">
                <Check className="w-4 h-4 text-brand-green flex-shrink-0 mt-0.5" />
                <span>{us}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// How It Works — 4 steps
// ---------------------------------------------------------------------------

const STEPS = [
  ["Set your rates",                    `Written reviews are priced by turnaround: ${REVIEW_TIERS.map((t) => `$${t.price} for ${t.label}`).join(", ")}. Live sessions are not offered yet; if they open, they will use the hourly rate you set.`],
  ["We surface you when AI hedges",     "When someone needs exactly your skill, the AI brief routes them to you. No marketing required."],
  ["Briefed client arrives",            "You see the AI conversation and the artifact before you accept. Accept or decline. No penalty for declining."],
  ["Deliver, sign off, get paid",       "Submit your written verdict. You keep 80%, one flat rate."],
];

function HowItWorks() {
  return (
    <section id="how" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
      <SectionHead
        eyebrow="How it works"
        title="Four steps from sign-up to first paid review."
      />
      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {STEPS.map(([title, body], i) => (
          <div key={i} className="bg-white border border-slateline-200 rounded-xl p-6">
            <div className="w-9 h-9 rounded-full bg-brand-greenPale text-brand-green font-display font-bold grid place-items-center">
              {i + 1}
            </div>
            <h3 className="font-display text-lg text-ink mt-4 leading-tight">{title}</h3>
            <p className="text-sm text-slateline-600 mt-2 leading-relaxed">{body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Categories: "open" only where a named reviewer covers the work today
// (the founder: code and product/strategy). Update when a specialist joins.
// ---------------------------------------------------------------------------

const CATEGORIES = [
  ["Shippers",      "active",     "Production engineers reviewing AI-built code, payments, infra, deploys."],
  ["Brainstormers", "active",     "Senior operators for strategy, ideation, naming, positioning calls."],
  ["Lawyers",       "recruiting", "Licensed attorneys reviewing AI-drafted SAFEs, NDAs, ToS, contracts."],
  ["Numbers",       "recruiting", "CPAs, CFAs, ex-CFOs reviewing financial models, tax positions, cap tables."],
  ["Marketeers",    "recruiting", "Working copywriters, brand operators, growth leaders reviewing GTM work."],
  ["Specialists",   "recruiting", "Domain-deep niches: healthcare, supplement regulation, clinical writing."],
  ["Vibe Coders",   "recruiting", "Senior engineers turning Lovable / v0 / Cursor prototypes into production."],
];

function Categories() {
  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
      <SectionHead
        eyebrow="What you'll review"
        title="Seven categories. Pick the one that fits."
        subtitle="Two are open today, reviewed by the founder. The others open as we add hand-picked specialists."
      />

      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
        {CATEGORIES.map(([name, status, desc]) => (
          <div key={name} className="bg-white border border-slateline-200 hover:border-slateline-300 rounded-xl p-5 transition-colors flex flex-col gap-2.5">
            <div className="flex items-center justify-between gap-2">
              <div className="font-display text-[17px] text-ink leading-tight">{name}</div>
              <span className={[
                "text-[10px] font-bold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border",
                status === "active"
                  ? "text-brand-green bg-brand-greenPale border-brand-green/30"
                  : "text-slateline-500 bg-slateline-50 border-slateline-200",
              ].join(" ")}>
                {status === "active" ? "Open" : "Not open yet"}
              </span>
            </div>
            <p className="text-[13px] leading-relaxed text-slateline-600">{desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Time commitment — 3 lanes, middle is featured
// ---------------------------------------------------------------------------

function TimeCommitment() {
  return (
    <section id="pricing" className="bg-white border-t border-b border-slateline-200">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <SectionHead
          eyebrow="Time commitment"
          title="Three lanes. Pick the one that fits your life."
        />

        <div className="mt-10 grid lg:grid-cols-3 gap-4">
          <TimeLane
            title="Try one review"
            body="Async, 1-week turnaround, no minimum. See if it's for you before you commit any availability."
            cadence="any time" hours="~1"
          />
          <TimeLane
            featured
            title="Side income"
            body="5–10 hours a week, you set the windows."
            cadence="weekly" hours="5–10"
          />
          <TimeLane
            title="Full bench"
            body="Daily availability for senior practitioners who want this to be a regular part of their week."
            cadence="daily" hours="15–25"
          />
        </div>
      </div>
    </section>
  );
}

function TimeLane({ title, body, cadence, hours, featured = false }) {
  return (
    <div className={[
      "rounded-xl p-7 flex flex-col gap-3 border",
      featured
        ? "bg-brand-greenPale border-brand-green/35"
        : "bg-white border-slateline-200",
    ].join(" ")}>
      <h3 className="font-display text-2xl text-ink leading-tight">{title}</h3>
      <p className="text-sm leading-relaxed text-slateline-600">{body}</p>
      <div className={[
        "flex gap-4 pt-3 mt-1 border-t text-xs text-slateline-500",
        featured ? "border-brand-green/20" : "border-slateline-100",
      ].join(" ")}>
        <div>Cadence: <b className="text-ink font-semibold">{cadence}</b></div>
        <div>Hours: <b className="text-ink font-semibold">{hours}</b></div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// FAQ — native <details>/<summary> with group-open rotation
// ---------------------------------------------------------------------------

const FAQ_ITEMS = [
  ["How fast do bookings come in once I'm listed?",
   "We're early, so we can't promise volume yet. Today the founder handles the async reviews himself; we add specialists by hand where demand shows up, and we'll be straight with you about it before you're listed."],
  ["What if I'm a licensed professional (lawyer, CPA, MD)?",
   "We verify credentials before listing. You set your own scope-of-practice and disclaimers; you maintain your own malpractice insurance. We do not provide UPL cover — your license is yours to manage."],
  ["Can I decline a booking?",
   "Yes, with no penalty. You see the AI brief and the artifact before you accept. If it's outside your scope or you don't have the bandwidth, just decline. We re-route."],
  ["How does payment work?",
   "You keep 80% (a flat 20% platform fee). Payouts go to the Stripe account you connect during onboarding, once Stripe has verified it."],
  ["Do I need malpractice insurance?",
   "For licensed practice (law, medicine, financial advisory), yes — same as anywhere. We do not provide it. For non-licensed advisory work, it's optional. Talk to your professional advisor for your situation."],
  ["Can I leave?",
   "Yes, any time, no notice. Existing in-flight bookings stay on the platform until delivered; after that you're off. Your profile and reviews are yours — we'll export them on request."],
];

function FAQ() {
  return (
    <section id="faq" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
      <SectionHead
        eyebrow="Common questions"
        title="Things experts ask first."
      />

      <div className="mt-10 bg-white border border-slateline-200 rounded-xl overflow-hidden">
        {FAQ_ITEMS.map(([q, a], i) => (
          <details key={i} className="group border-b border-slateline-200 last:border-b-0">
            <summary className="cursor-pointer list-none flex items-center justify-between gap-4 px-7 py-5 font-display text-[17px] text-ink">
              <span>{q}</span>
              <span className="text-slateline-400 text-2xl font-light leading-none transition-transform duration-200 group-open:rotate-45 select-none">
                +
              </span>
            </summary>
            <div className="px-7 pb-5 text-sm leading-relaxed text-slateline-600">
              {a}
            </div>
          </details>
        ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Apply — final dark band CTA + existing form
// ---------------------------------------------------------------------------

function ApplySection(props) {
  const {
    user, loading, uploading, categories,
    imageOption, setImageOption, fileInputRef,
    formData, setFormData,
    toggleListItem, handleImageUpload, handleSubmit, acceptanceError,
    acceptedExpertLegal, setAcceptedExpertLegal,
    reviewerRole,
  } = props;

  return (
    <section id="apply" className="relative bg-ink text-white overflow-hidden">
      <div aria-hidden="true"
           className="absolute -bottom-32 -left-24 w-[640px] h-[640px] rounded-full bg-brand-green/25 blur-3xl pointer-events-none" />
      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand-green text-center">
          The expert side of the human review layer
        </p>
        <h2 className="font-display text-4xl sm:text-5xl text-white leading-tight mt-3 text-center">
          Your expertise. AI's distribution.<br />
          <em className="not-italic">
            <span className="italic text-brand-green">On your schedule.</span>
          </em>
        </h2>
        <p className="text-white/70 mt-5 text-center max-w-xl mx-auto">
          Apply in three minutes. We review every application by hand and reply by email.
        </p>
        {reviewerRole && (
          <p data-testid="reviewer-role-note"
             className="mt-4 text-center text-sm text-brand-green font-semibold">
            Applying as a reviewer: a paid calibration review follows your application.
          </p>
        )}

        <div className="mt-12">
          {!user ? (
            <div className="bg-white text-ink rounded-xl p-10 text-center shadow-lg">
              <h3 className="font-display text-2xl">Sign in to apply.</h3>
              <p className="text-slateline-600 mt-3">
                Already have an Xpert account? Sign in. Otherwise create one — it takes 30 seconds.
              </p>
              <div className="mt-6 flex gap-3 justify-center">
                <Link to={reviewerRole ? `/login?return_to=${REVIEWER_RETURN_TO}` : "/login"}
                      className="inline-flex h-11 items-center px-5 rounded-md bg-brand-green hover:bg-brand-greenHover text-white font-semibold text-sm shadow-sm">
                  Sign in
                </Link>
                <Link to="/register"
                      className="inline-flex h-11 items-center px-5 rounded-md border border-slateline-300 hover:border-ink text-ink font-medium text-sm">
                  Create account
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-8 text-ink">
              <FormSection title="1 · Your profile" subtitle="The basics that appear on your expert card.">
                <Field label="Professional title *">
                  <Input value={formData.title}
                         onChange={(v) => setFormData((p) => ({ ...p, title: v }))}
                         placeholder="Senior Software Architect · Ex-Google" />
                </Field>
                <Field label="Bio *">
                  <textarea
                    value={formData.bio}
                    onChange={(e) => setFormData((p) => ({ ...p, bio: e.target.value }))}
                    rows={5} maxLength={1000}
                    placeholder="Tell clients who you are, what you've shipped, what you'll catch that AI won't."
                    className={textareaCls}
                  />
                  <div className="text-xs text-slateline-400 mt-1.5 text-right">
                    {formData.bio.length} / 1000
                  </div>
                </Field>

                <Field label="LinkedIn (recommended)">
                  <Input value={formData.linkedin}
                         onChange={(v) => setFormData((p) => ({ ...p, linkedin: v }))}
                         placeholder="https://linkedin.com/in/…" />
                </Field>

                <Field label="Profile photo">
                  <ImageUploader
                    imageOption={imageOption} setImageOption={setImageOption}
                    fileInputRef={fileInputRef} onUpload={handleImageUpload}
                    uploading={uploading} formData={formData} setFormData={setFormData}
                  />
                </Field>
              </FormSection>

              <FormSection title="2 · Expertise" subtitle="What you'll review. Pick the launch categories you can sign off on.">
                <Field label="Review categories (V1 launch) *">
                  <ChipPicker
                    options={V1_CATEGORIES}
                    selected={formData.review_categories}
                    onToggle={(v) => toggleListItem("review_categories", v)}
                  />
                </Field>

                <Field label="Industries">
                  <ChipPicker
                    options={categories.industries || []}
                    selected={formData.industries}
                    onToggle={(v) => toggleListItem("industries", v)}
                    emptyHint="Industries will populate from the server."
                  />
                </Field>

                <Field label="Specific expertise tags *">
                  <ChipPicker
                    options={categories.expertise || []}
                    selected={formData.expertise}
                    onToggle={(v) => toggleListItem("expertise", v)}
                    emptyHint="Expertise tags will populate from the server."
                  />
                </Field>

                <Field label="AI tools you're comfortable reviewing">
                  <ChipPicker
                    options={AI_TOOLS}
                    selected={formData.review_ai_tools}
                    onToggle={(v) => toggleListItem("review_ai_tools", v)}
                  />
                  <p className="text-xs text-slateline-500 mt-2 leading-relaxed">
                    We route submissions from these AI tools to you first.
                    You don't need to know every tool — just check the ones whose
                    output you can confidently judge.
                  </p>
                </Field>
              </FormSection>

              <FormSection title="3 · Pricing & availability" subtitle="Set what you charge and how you want to work.">
                <div className="flex items-start gap-3 p-4 rounded-lg bg-brand-greenPale/40 border border-brand-green/30">
                  <input
                    type="checkbox"
                    checked={formData.review_available}
                    onChange={(e) => setFormData((p) => ({ ...p, review_available: e.target.checked }))}
                    className="mt-1 h-4 w-4 rounded border-slateline-300 text-brand-green focus:ring-brand-green/30"
                    id="review_available"
                  />
                  <label htmlFor="review_available" className="cursor-pointer">
                    <div className="font-display text-base text-ink leading-tight inline-flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-brand-green" />
                      Available for AI Review
                    </div>
                    <p className="mt-1 text-sm text-slateline-600 leading-relaxed">
                      Get assigned async AI review submissions from clients in
                      your domain. Live sessions aren't offered yet.
                    </p>
                  </label>
                </div>

                <FormRow>
                  <Field label="Hourly rate (used only if live sessions open) *">
                    <Input value={formData.hourly_rate}
                           type="number"
                           onChange={(v) => setFormData((p) => ({ ...p, hourly_rate: v }))}
                           placeholder="200" />
                  </Field>
                  <Field label="Currency">
                    <Select value={formData.currency}
                            onChange={(v) => setFormData((p) => ({ ...p, currency: v }))}
                            options={CURRENCIES.map((c) => [c.value, c.label])} />
                  </Field>
                </FormRow>

                <Field label="Async review price (per submission, USD)">
                  <Input value={formData.review_price_usd}
                         type="number"
                         onChange={(v) => setFormData((p) => ({ ...p, review_price_usd: v }))}
                         placeholder="79" />
                  <p className="text-xs text-slateline-500 mt-2 leading-relaxed">
                    Today buyers pay Xpert's list prices for async reviews (from
                    ${REVIEW_PRICE_FROM}, by turnaround), whatever you enter here.
                  </p>
                </Field>

                <Field label="Custom meeting link (optional)">
                  <Input value={formData.meeting_link}
                         onChange={(v) => setFormData((p) => ({ ...p, meeting_link: v }))}
                         placeholder="https://meet.google.com/… or use ours" />
                </Field>

                <div className="flex items-start gap-3 pt-2">
                  <input
                    type="checkbox"
                    checked={formData.stay_anonymous}
                    onChange={(e) => setFormData((p) => ({ ...p, stay_anonymous: e.target.checked }))}
                    className="mt-1 h-4 w-4 rounded border-slateline-300 text-brand-green focus:ring-brand-green/30"
                    id="stay_anonymous"
                  />
                  <label htmlFor="stay_anonymous" className="cursor-pointer">
                    <div className="text-sm font-medium text-ink">Stay anonymous publicly</div>
                    <div className="text-xs text-slateline-500 mt-1">
                      Initials only on your expert card. Clients still see your full name once matched.
                    </div>
                  </label>
                </div>
              </FormSection>

              <div className="pt-2 space-y-4">
                <div className="bg-canvas/95 border border-slateline-200 rounded-md p-4">
                  <LegalAcceptanceCheckbox
                    policies={[
                      { id: "terms_of_service",   label: "Terms of Service",        href: "/terms" },
                      { id: "privacy_policy",     label: "Privacy Policy",          href: "/privacy" },
                      { id: "expert_agreement",   label: "Expert Agreement",        href: "/legal/expert-agreement" },
                      { id: "recording_consent",  label: "Recording & AI Consent",  href: "/legal/recording-consent" },
                    ]}
                    checked={acceptedExpertLegal}
                    onChange={setAcceptedExpertLegal}
                  />
                </div>
                {acceptanceError && (
                  <div role="alert"
                       className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
                    <p className="font-semibold">Your application was not submitted.</p>
                    <p className="mt-1">{acceptanceError}</p>
                  </div>
                )}
                <button
                  type="submit" disabled={loading || !acceptedExpertLegal}
                  className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-md
                             bg-brand-green hover:bg-brand-greenHover disabled:opacity-60
                             text-white font-semibold text-sm tracking-wide shadow-sm transition-colors"
                >
                  {loading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4" />
                      Submit application
                    </>
                  )}
                </button>
                <p className="text-xs text-white/60 mt-3 text-center">
                  You'll be notified by email once your credentials are verified.
                </p>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Shared section header
// ---------------------------------------------------------------------------

function SectionHead({ eyebrow, title, subtitle }) {
  return (
    <div className="max-w-3xl">
      <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand-green">{eyebrow}</p>
      <h2 className="font-display text-4xl sm:text-[44px] text-ink leading-[1.1] mt-4">{title}</h2>
      {subtitle && (
        <p className="text-lg text-slateline-600 mt-4 leading-relaxed">{subtitle}</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Form primitives (unchanged from prior version)
// ---------------------------------------------------------------------------

function FormSection({ title, subtitle, children }) {
  return (
    <section className="bg-white border border-slateline-200 rounded-xl p-6 sm:p-8 shadow-sm space-y-6">
      <header>
        <h3 className="font-display text-2xl text-ink leading-tight">{title}</h3>
        {subtitle && <p className="text-sm text-slateline-600 mt-1.5">{subtitle}</p>}
      </header>
      {children}
    </section>
  );
}

function FormRow({ children }) {
  return <div className="grid sm:grid-cols-2 gap-4">{children}</div>;
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider text-slateline-500">
        {label}
      </label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

const inputCls = "w-full bg-white border border-slateline-200 rounded-md px-3 h-11 " +
  "text-ink placeholder-slateline-400 outline-none " +
  "focus:border-brand-green focus:ring-2 focus:ring-brand-green/20 transition-colors";
const textareaCls = inputCls.replace("h-11", "min-h-[120px] py-3");

function Input({ value, onChange, placeholder, type = "text" }) {
  return (
    <input
      type={type} value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={inputCls}
    />
  );
}

function Select({ value, onChange, options }) {
  return (
    <select value={value}
            onChange={(e) => onChange(e.target.value)}
            className={`${inputCls} pr-8`}>
      {options.map(([val, label]) => (
        <option key={val} value={val}>{label}</option>
      ))}
    </select>
  );
}

function ChipPicker({ options, selected, onToggle, emptyHint }) {
  if (!options.length) {
    return <p className="text-xs text-slateline-400">{emptyHint || "No options available."}</p>;
  }
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const active = selected.includes(opt);
        return (
          <button
            key={opt} type="button"
            onClick={() => onToggle(opt)}
            className={[
              "px-3 py-1.5 rounded-full text-sm border transition-all",
              active
                ? "border-ink bg-ink text-white"
                : "border-slateline-300 text-slateline-600 hover:border-ink/40 hover:text-ink",
            ].join(" ")}
          >
            {opt}
            {active && <X className="inline-block w-3 h-3 ml-1.5" />}
          </button>
        );
      })}
    </div>
  );
}

function ImageUploader({ imageOption, setImageOption, fileInputRef, onUpload, uploading, formData, setFormData }) {
  return (
    <div>
      <div className="flex gap-2 mb-3">
        {["upload", "url"].map((opt) => (
          <button
            key={opt} type="button"
            onClick={() => setImageOption(opt)}
            className={[
              "px-3 py-1.5 rounded-full text-xs font-semibold border transition-all",
              imageOption === opt
                ? "border-ink bg-ink text-white"
                : "border-slateline-300 text-slateline-600",
            ].join(" ")}
          >
            {opt === "upload" ? "Upload file" : "Use URL"}
          </button>
        ))}
      </div>

      {imageOption === "upload" ? (
        <div>
          <input
            ref={fileInputRef} type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={onUpload}
            className="hidden"
          />
          <button
            type="button" disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
            className="w-full h-32 rounded-md border-2 border-dashed border-slateline-300
                       hover:border-ink/30 bg-canvas hover:bg-white
                       flex flex-col items-center justify-center gap-2 text-slateline-500 hover:text-ink
                       transition-all"
          >
            {uploading ? (
              <div className="w-5 h-5 border-2 border-slateline-400 border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <Upload className="w-5 h-5" />
                <span className="text-sm">Click to upload (JPG/PNG/WebP, max 5MB)</span>
              </>
            )}
          </button>
        </div>
      ) : (
        <Input value={formData.image_url}
               onChange={(v) => setFormData((p) => ({ ...p, image_url: v }))}
               placeholder="https://…" />
      )}

      {formData.image_url && (
        <div className="mt-3 flex items-center gap-3 p-3 bg-canvas rounded-md border border-slateline-200">
          <img src={formData.image_url} alt="preview"
               className="w-12 h-12 rounded-full object-cover" />
          <div className="text-sm text-ink truncate flex-1">{formData.image_url}</div>
          <button type="button"
                  onClick={() => setFormData((p) => ({ ...p, image_url: "" }))}
                  className="text-slateline-500 hover:text-rose-600">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}
